import { useGetDialogs } from '@yuno/dashboard-api-mfe'
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import { ContainerContext } from '../../context/container-context'
import DrawerItem from '../drawer/drawer-item'
import Account from './account'
import { sections } from '../../utils/constants'
import { useBreadcrumb } from './hooks/breadcrumb'
import { useMenu } from './hooks/menu'
import { useSnackbar } from './hooks/snackbar/snackbar.hook'

import { NotificationsComponent } from '../notifications'
import {
  AppBar,
  Drawer,
  Switch,
  Box,
  TIcons,
  Icon,
  useTheme,
  Theme,
} from '@yuno/design-system-web'
import { useProfile } from './account/account.hooks'
import { OrganizationsList } from '../organizations/organizations-list'
import useHandleSwitch from './use-handle-switch'
import { useTranslation } from 'react-i18next'
import { ButtonProps } from './layout-types'
import { useMediaQuery } from '@mui/material'
import { useIsTesting } from '../../yuno-dashboard-container-mfe'
import { IconButtonStyled } from './styles'
import { LoadingPulse } from '../loading-pulse'

interface Props {
  setOpenDialog: Dispatch<SetStateAction<boolean>>
  setTitle: Dispatch<SetStateAction<string>>
  setContent: Dispatch<SetStateAction<string>>
  setButtons: Dispatch<SetStateAction<ButtonProps[]>>
  allowed: boolean
  infoHeader: { icon: TIcons; message: string }
}

const AppBarLayout = ({
  setOpenDialog,
  setTitle,
  setContent,
  setButtons,
  allowed,
  infoHeader,
}: Props) => {
  const { items, ready } = useMenu()

  const { isTesting } = useIsTesting()
  const [isPrincipal, setIsPrincipal] = useState(false)
  const {
    organization,
    testingEnvironment,
    openDrawer,
    handleToggleDrawer,
    accounts,
    isLoadingAccount,
    organizations,
    error,
    success,
  } = useContext(ContainerContext)
  const matches = useMediaQuery('(max-width: 1023px)')
  const isMobile = useMediaQuery('(max-width: 599px)')

  const theme: Theme = useTheme()
  const handleOpenDialog = () => setOpenDialog((prev) => !prev)
  const response = useGetDialogs()
  const { t } = useTranslation()
  const { section, path, handleSection } = useBreadcrumb()
  const { onOpenSnackbar } = useSnackbar()
  const { profile, isLoadingProfile, customer } = useProfile()
  const [hover, setHover] = useState(false)

  useEffect(() => {
    if (!isLoadingProfile && profile)
      localStorage.setItem('userSettings', JSON.stringify(profile?.settings))
  }, [profile, isLoadingProfile, location.pathname])

  const handleSwitch = useHandleSwitch({
    allowed,
    handleOpenDialog,
    setTitle,
    setContent,
    response,
    setButtons,
    t,
  })

  const setPrincipal = (value) => setIsPrincipal(value)

  const handleNavigation = () => {
    const menuSection = Object.entries(sections).filter(
      (item) => item[1].title === section,
    )
    const isMenuSection = menuSection.length > 0
    const currentSection = menuSection[0]
    if (path) {
      ;(window as any).singleSpaNavigate(
        isMenuSection ? sections[`${currentSection[0]}`].path : path,
      )
    }
  }

  if (!ready) return <LoadingPulse />

  return (
    <div
      onMouseEnter={setHover.bind(null, true)}
      onMouseLeave={setHover.bind(null, false)}
    >
      {hover && !matches && (
        <IconButtonStyled
          onClick={handleToggleDrawer}
          open={openDrawer}
          isTesting={isTesting}
        >
          <Icon
            className="drawer-menu-icon"
            name={openDrawer ? 'ArrowCircleLeft' : 'ArrowCircleRight'}
            weight="fill"
            color={theme.palette.primary[1]}
            width="16px"
            height="16px"
            preserveAspectRatio="xMidYMid slice"
          />
        </IconButtonStyled>
      )}
      <AppBar
        open={openDrawer}
        position="relative"
        onToggle={handleToggleDrawer}
        infoHeader={infoHeader}
        section={section}
        handleNavigation={handleNavigation}
      >
        <>
          {organizations.data?.length > 0 && (
            <OrganizationsList
              options={organizations.data}
              organization={organization}
              error={error}
              success={success}
              accounts={accounts}
              onOpenSnackbar={onOpenSnackbar}
              setPrincipal={setPrincipal}
            />
          )}

          {!isMobile && (
            <>
              <Box paddingRight={'38px'}>
                {!isPrincipal && (
                  <Switch
                    checked={testingEnvironment}
                    onClick={handleSwitch}
                    label={`${t('testMode')} · ${
                      testingEnvironment ? t('on') : t('off')
                    }`}
                    labelPlacement="start"
                  />
                )}
              </Box>
            </>
          )}
          <NotificationsComponent customer={customer} />
          <Account
            checkedSwitch={testingEnvironment}
            handleSwitch={handleSwitch}
            profile={profile}
            handleSection={handleSection}
            accounts={accounts}
            isLoadingProfile={isLoadingProfile}
            isLoadingAccounts={isLoadingAccount}
            hidden={isPrincipal}
          />
        </>
      </AppBar>

      <Drawer
        handleToggleDrawer={handleToggleDrawer}
        user={profile}
        open={openDrawer}
        variant={matches ? 'temporary' : 'permanent'}
        onClick={() => {
          ;(window as any).singleSpaNavigate('/')
        }}
        sx={testingEnvironment ? { top: '40px' } : { top: 0 }}
        items={
          <div>
            {items?.map((el, key) => (
              <DrawerItem
                key={key}
                index={key}
                item={el}
                open={openDrawer}
                onClick={el.onClick}
                isPrincipalOrganization={isPrincipal}
                submenues={el.submenues}
              />
            ))}
          </div>
        }
      />
    </div>
  )
}
export default AppBarLayout
